import React, { useCallback, useState } from 'react';
import {
  AppButton,
  Box,
  FormProvider,
  getValidationResolver,
  OptionKey,
  RawButton,
  SubmitHandler,
  Text,
  useForm,
} from '@common-fe/common-fe';

import { VALIDATORS } from '@/common';
import { PendingModal } from '@/components/elements';
import Modal from '@/components/elements/AutoBlockedModal/Modal';
import { IPermissionsSection } from '@/modules/role/role.types';

import { useRoleSetupStore } from '../../stores';

import CopyPermissionsSection from './CopyPermissionsSection';

interface Props {
  onApply: (values: OptionKey[]) => void;
}
const PermissionsCopyModal: React.FC<Props> = ({ onApply }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isConfirm, setIsConfirm] = useState(false);
  const [permissionIds, setPermissionIds] = useState<OptionKey[]>([]);
  const methods = useForm<IPermissionsSection>({
    defaultValues: {
      isCopyRole: true,
    },
    resolver: getValidationResolver({
      copyRoleId: VALIDATORS.STRING.when('isCopyRole', {
        is: (val?: boolean) => {
          return val === true;
        },
        then: () => VALIDATORS.REQUIRED_STRING,
        otherwise: () => VALIDATORS.STRING,
      }),
    }),
  });

  const setState = useRoleSetupStore((state) => state.setState);
  const handleSubmit: SubmitHandler<IPermissionsSection> = useCallback((data) => {
    // setIsVisible(false);
    setIsConfirm(true);
  }, []);
  const handleConfirmClose = useCallback(() => {
    setIsConfirm(false);
    // setIsVisible((true));
  }, []);
  const handleConfirm = useCallback(() => {
    setIsVisible(false);
    setIsConfirm(false);
    setState({
      permissions: permissionIds,
    });

    onApply(permissionIds);

    methods.reset();
  }, [methods, onApply, permissionIds, setState]);

  return (
    <>
      <Modal testId="permission-copy" visible={isVisible} onSetVisible={setIsVisible}>
        <PendingModal
          visible={isConfirm}
          onSetVisible={setIsConfirm}
          header="Override existing permissions?"
          helptext=""
        >
          <Box direction="row" align="center">
            <AppButton width="180px" onClick={handleConfirmClose} buttonType="secondary">
              Cancel
            </AppButton>
            <Box width="s" />
            <AppButton width="180px" buttonType="primary" onClick={handleConfirm} color="red">
              Yes, Override
            </AppButton>
          </Box>
        </PendingModal>
        <FormProvider {...methods}>
          <form name="permissionsCopy" onSubmit={methods.handleSubmit(handleSubmit)}>
            <Box>
              <Text textAlign="center" size="2xl" weight={700} margin={{ bottom: 'xs' }}>
                Copy Permissions
              </Text>
              <Text textAlign="center">
                Quickly create new roles by copying permissions from an existing one. Any permissions you set
                manually will take precedence over the template's settings.
              </Text>
            </Box>
            <Box round="moduleRound" background="module" pad="l">
              <CopyPermissionsSection isStandAloneMode isEditMode onChangeValues={setPermissionIds} />
            </Box>
            <Box direction="row" justify="end" margin={{ top: 'l' }}>
              <AppButton width="160px" onClick={() => setIsVisible(false)} buttonType="secondary">
                Cancel
              </AppButton>
              <Box margin={{ left: 's' }}>
                <AppButton width="160px" type="submit">
                  Apply (1/2)
                </AppButton>
              </Box>
            </Box>
          </form>
        </FormProvider>
      </Modal>
      <Box width={{ min: '120px' }}>
        <RawButton onClick={() => setIsVisible(true)}>
          <Text weight={400} color="textAccent">
            Copy Permissions
          </Text>
        </RawButton>
      </Box>
    </>
  );
};

export default PermissionsCopyModal;
